import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import {
  deleteLicenseRate,
  deleteLicensedDesign,
  getSpLicenseSettings,
  listLicenseRates,
  listLicensedDesigns,
  loadSpLicenseReport,
  saveLicenseRate,
  saveLicensedDesign,
  saveSpLicenseSettings,
  setLineLicenseDesign
} from '../utils/spLicenseStore';
import {
  dueDateForMonth,
  formatLongDate,
  formatMonthLabel,
  previousMonthKey,
  retailerProblems,
  rowsToCsv,
  validateSettings
} from '../utils/spLicenseReport';

const ACCENT = 'var(--al-primary)';
const PAGE_BG = 'var(--al-bg)';
const CARD_BG = 'var(--al-surface)';

const Page = styled.div`
  min-height: 100vh;
  background: ${PAGE_BG};
  color: #fff;
  padding: 88px 24px 64px;
`;

const Inner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 1.8rem;
  font-weight: 600;
  color: ${ACCENT};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  max-width: 720px;
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.45;
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
`;

const TabButton = styled.button`
  background: ${(p) => (p.$active ? 'rgba(152, 251, 152, 0.16)' : 'transparent')};
  color: ${(p) => (p.$active ? ACCENT : 'rgba(255,255,255,0.7)')};
  border: 1px solid ${(p) => (p.$active ? ACCENT : 'rgba(255,255,255,0.15)')};
  border-radius: 999px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 0.85rem;
`;

const Card = styled.section`
  background: ${CARD_BG};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
`;

const CardTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 1rem;
  font-weight: 600;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 12px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  min-width: ${(p) => p.$wide ? '240px' : '140px'};
  flex: ${(p) => (p.$grow ? '1 1 180px' : '0 1 auto')};
`;

const Input = styled.input`
  background: #1c1c1c;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.9rem;
`;

const Button = styled.button`
  background: ${(p) => (p.$ghost ? 'transparent' : ACCENT)};
  color: ${(p) => (p.$ghost ? ACCENT : '#102010')};
  border: 1px solid ${ACCENT};
  border-radius: 8px;
  padding: 8px 14px;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Danger = styled.button`
  background: transparent;
  color: #ffb4a8;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const Note = styled.p`
  margin: 0 0 12px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.88rem;
  line-height: 1.45;
`;

const Warn = styled.div`
  background: rgba(255, 180, 168, 0.1);
  border: 1px solid rgba(255, 180, 168, 0.35);
  color: #ffd0c8;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
  font-size: 0.9rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  th, td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    vertical-align: top;
  }
  th {
    color: rgba(255, 255, 255, 0.5);
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  select {
    background: #1c1c1c;
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    padding: 4px 6px;
  }
`;

const Muted = styled.span`
  color: rgba(255, 255, 255, 0.45);
`;

const SearchList = styled.div`
  max-height: 180px;
  overflow: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-top: 8px;
`;

const SearchItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  color: #fff;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding: 8px 10px;
  cursor: pointer;
  &:hover { background: rgba(255, 255, 255, 0.05); }
`;

const emptyDesign = {
  designName: '',
  upi: '',
  licenseCode: '',
  institutionShortCode: '',
  categorySubCategoryCode: '',
  prodDescription: '',
  associatedInst: '',
  garmentNote: ''
};

function money(n) {
  return Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function downloadCsv(filename, text) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function SpLicenseReporting() {
  const [tab, setTab] = useState('report');
  const [month, setMonth] = useState(previousMonthKey());
  const [settings, setSettings] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [rates, setRates] = useState([]);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState('');
  const [designForm, setDesignForm] = useState(emptyDesign);
  const [editingId, setEditingId] = useState(null);
  const [designError, setDesignError] = useState('');
  const [savingDesign, setSavingDesign] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerQuery, setCustomerQuery] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [rateForm, setRateForm] = useState({
    institutionShortCode: '',
    categoryCode: '',
    royaltyPercent: '',
    mruAmount: ''
  });
  const [rateError, setRateError] = useState('');

  const refreshCatalog = useCallback(async () => {
    const [nextSettings, nextDesigns, nextRates] = await Promise.all([
      getSpLicenseSettings(db),
      listLicensedDesigns(db),
      listLicenseRates(db)
    ]);
    setSettings(nextSettings);
    setDesigns(nextDesigns);
    setRates(nextRates);
  }, []);

  useEffect(() => {
    refreshCatalog().catch((err) => {
      console.error(err);
      setReportError('Could not load license settings.');
    });
  }, [refreshCatalog]);

  useEffect(() => {
    if (tab !== 'settings' || customers.length > 0) return;
    getDocs(collection(db, 'customers'))
      .then((snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.client || '').localeCompare(String(b.client || '')));
        setCustomers(list);
      })
      .catch((err) => console.error(err));
  }, [tab, customers.length]);

  const loadReport = async () => {
    setLoadingReport(true);
    setReportError('');
    try {
      const result = await loadSpLicenseReport(db, month);
      setSettings(result.settings);
      setDesigns(result.designs);
      setRates(result.rates);
      setReport({ ...result, loadedMonth: month });
    } catch (err) {
      console.error(err);
      setReportError('Could not load orders for this month.');
    } finally {
      setLoadingReport(false);
    }
  };

  const storeProblems = retailerProblems(settings);
  const rowProblems = (report?.rows || []).filter((row) => row.issues.length > 0);
  const canExport = Boolean(
    report &&
    report.loadedMonth === month &&
    !report.needsCustomer &&
    report.rows.length > 0 &&
    rowProblems.length === 0 &&
    storeProblems.length === 0
  );

  const totals = useMemo(() => {
    const rows = report?.rows || [];
    return {
      units: rows.reduce((sum, row) => sum + row.units, 0),
      gross: rows.reduce((sum, row) => sum + row.gross, 0)
    };
  }, [report]);

  const filteredCustomers = customerQuery.trim()
    ? customers.filter((customer) => (
      String(customer.client || '').toLowerCase().includes(customerQuery.trim().toLowerCase())
    )).slice(0, 30)
    : [];

  const onDesignChange = (field) => (event) => {
    const value = event.target.value;
    setDesignForm((prev) => {
      const next = { ...prev, [field]: value };
      if (
        field === 'institutionShortCode' &&
        (!prev.licenseCode || prev.licenseCode === prev.institutionShortCode)
      ) {
        next.licenseCode = value;
      }
      return next;
    });
  };

  const startEdit = (design) => {
    setEditingId(design.id);
    setDesignForm({
      designName: design.designName || '',
      upi: design.upi || '',
      licenseCode: design.licenseCode || '',
      institutionShortCode: design.institutionShortCode || '',
      categorySubCategoryCode: design.categorySubCategoryCode || '',
      prodDescription: design.prodDescription || '',
      associatedInst: design.associatedInst || '',
      garmentNote: design.garmentNote || ''
    });
    setDesignError('');
    setTab('designs');
  };

  const saveDesign = async () => {
    setSavingDesign(true);
    setDesignError('');
    try {
      const result = await saveLicensedDesign(db, designForm, editingId);
      if (!result.ok) {
        setDesignError(result.error);
        return;
      }
      setDesignForm(emptyDesign);
      setEditingId(null);
      await refreshCatalog();
    } catch (err) {
      console.error(err);
      setDesignError('Could not save this design.');
    } finally {
      setSavingDesign(false);
    }
  };

  const removeDesign = async (design) => {
    if (!window.confirm(`Remove ${design.designName}?`)) return;
    await deleteLicensedDesign(db, design.id);
    if (editingId === design.id) {
      setEditingId(null);
      setDesignForm(emptyDesign);
    }
    await refreshCatalog();
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    setSettingsError('');
    try {
      const error = validateSettings(settings);
      if (error) {
        setSettingsError(error);
        return;
      }
      const saved = await saveSpLicenseSettings(db, settings);
      setSettings((prev) => ({
        ...prev,
        customerId: saved.customerId,
        customerName: saved.customerName,
        retailerName: saved.retailerName,
        clcRetailerCode: saved.clcRetailerCode,
        address: saved.address,
        city: saved.city,
        state: saved.state,
        zip: saved.zip
      }));
    } catch (err) {
      console.error(err);
      setSettingsError('Could not save the store profile.');
    } finally {
      setSavingSettings(false);
    }
  };

  const addRate = async () => {
    setRateError('');
    const result = await saveLicenseRate(db, rateForm);
    if (!result.ok) {
      setRateError(result.error);
      return;
    }
    setRateForm({ institutionShortCode: '', categoryCode: '', royaltyPercent: '', mruAmount: '' });
    setRates(await listLicenseRates(db));
  };

  const chooseDesign = async (row, designId) => {
    await setLineLicenseDesign(db, row.lineId, designId);
    await loadReport();
  };

  const exportCsv = () => {
    if (!canExport) return;
    const csv = rowsToCsv(report.rows, settings);
    downloadCsv(`clc_royalty_${report.loadedMonth}.csv`, csv);
  };

  const due = dueDateForMonth(month);

  return (
    <Page>
      <Inner>
        <Title>SP license reporting</Title>
        <Subtitle>
          Royalty file for orders placed by the Strawberry Patch retail customer.
          Work produced at the Strawberry Patch facility for other customers stays out of this report.
          Upload the CSV in Brand Manager 360. CLC calculates the amount due.
        </Subtitle>

        <Tabs>
          {['report', 'designs', 'settings'].map((key) => (
            <TabButton key={key} type="button" $active={tab === key} onClick={() => setTab(key)}>
              {key === 'report' ? 'Monthly report' : key === 'designs' ? 'Licensed designs' : 'Store and rates'}
            </TabButton>
          ))}
        </Tabs>

        {tab === 'report' && (
          <>
            <Card>
              <Row>
                <Field>
                  Month
                  <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
                </Field>
                <Button type="button" onClick={loadReport} disabled={loadingReport || !month}>
                  {loadingReport ? 'Loading…' : `Load ${formatMonthLabel(month)}`}
                </Button>
                <Button type="button" onClick={exportCsv} disabled={!canExport}>
                  Export CLC CSV
                </Button>
              </Row>
              <Note>
                {formatMonthLabel(month)} is due {due ? formatLongDate(due) : 'on the 20th of the following month'}.
                Price is the wholesale on the order line. Retailer on every row is the store profile.
                Returns are not included.
              </Note>
              {settings && !settings.customerId && (
                <Warn>
                  Link the Strawberry Patch retail customer under Store and rates before loading orders.
                </Warn>
              )}
              {reportError && <Warn>{reportError}</Warn>}
              {report?.needsCustomer && (
                <Warn>Choose the Strawberry Patch customer, then load this month again.</Warn>
              )}
              {report && !report.needsCustomer && storeProblems.length > 0 && (
                <Warn>
                  Store profile still needs {storeProblems.join(', ')} before the file can be exported.
                </Warn>
              )}
              {report && !report.needsCustomer && report.rows.length === 0 && !loadingReport && (
                <Note>
                  No invoiced Strawberry Patch retail orders in {formatMonthLabel(month)}.
                  Submit a no-sales report in Brand Manager 360.
                </Note>
              )}
              {report && report.rows.length > 0 && (
                <Note>
                  {report.orders.length} order{report.orders.length === 1 ? '' : 's'} · {totals.units} units · {money(totals.gross)} wholesale
                </Note>
              )}
            </Card>

            {report && report.rows.length > 0 && (
              <Card>
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th>Design</th>
                        <th>UPI</th>
                        <th>School</th>
                        <th>Units</th>
                        <th>Gross</th>
                        <th>Royalty sales</th>
                        <th>MR/U</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.map((row) => (
                        <tr key={row.lineId}>
                          <td>
                            {row.invoiceNumber}
                            <div><Muted>{row.invoiceDate}</Muted></div>
                          </td>
                          <td>{row.designName}</td>
                          <td>{row.upi || '—'}</td>
                          <td>{row.institution || '—'}{row.category ? ` · ${row.category}` : ''}</td>
                          <td>{row.units}</td>
                          <td>{money(row.gross)}</td>
                          <td>{money(row.royaltySales)}</td>
                          <td>{row.mruUnits}</td>
                          <td>
                            {row.issues.length === 0 && <span>Ready</span>}
                            {row.issues.map((issue) => (
                              <div key={issue}>{issue}</div>
                            ))}
                            {row.mruNote && <div><Muted>{row.mruNote}</Muted></div>}
                            {row.choices.length > 1 && (
                              <select
                                value={row.selectedDesignId}
                                onChange={(e) => chooseDesign(row, e.target.value)}
                              >
                                <option value="">Choose item</option>
                                {row.choices.map((choice) => (
                                  <option key={choice.id} value={choice.id}>{choice.label}</option>
                                ))}
                              </select>
                            )}
                            {row.issues.some((issue) => issue.startsWith('No UPI')) && (
                              <div>
                                <Danger type="button" onClick={() => {
                                  setDesignForm({ ...emptyDesign, designName: row.designName });
                                  setEditingId(null);
                                  setTab('designs');
                                }}>
                                  Add this design
                                </Danger>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              </Card>
            )}
          </>
        )}

        {tab === 'designs' && (
          <>
            <Card>
              <CardTitle>{editingId ? 'Edit licensed design' : 'New licensed design'}</CardTitle>
              <Note>
                Create the UPI before the design can be printed on a Strawberry Patch retail order.
                One row per design. If the same art is later printed on a second CLC category, add another row with a garment note.
              </Note>
              {designError && <Warn>{designError}</Warn>}
              <Row>
                <Field $grow>
                  Design name
                  <Input value={designForm.designName} onChange={onDesignChange('designName')} placeholder="LeaningCy-2026" />
                </Field>
                <Field $grow>
                  UPI
                  <Input value={designForm.upi} onChange={onDesignChange('upi')} placeholder="LC20261467" />
                </Field>
              </Row>
              <Row>
                <Field $grow>
                  School code
                  <Input value={designForm.institutionShortCode} onChange={onDesignChange('institutionShortCode')} />
                </Field>
                <Field $grow>
                  License code
                  <Input value={designForm.licenseCode} onChange={onDesignChange('licenseCode')} />
                </Field>
                <Field $grow>
                  Category / subcategory
                  <Input value={designForm.categorySubCategoryCode} onChange={onDesignChange('categorySubCategoryCode')} placeholder="01A-1" />
                </Field>
              </Row>
              <Row>
                <Field $grow>
                  Product description
                  <Input value={designForm.prodDescription} onChange={onDesignChange('prodDescription')} placeholder="No commas" />
                </Field>
                <Field $grow>
                  Garment note
                  <Input value={designForm.garmentNote} onChange={onDesignChange('garmentNote')} placeholder="Tee, only if needed" />
                </Field>
              </Row>
              <Field $wide>
                Associated schools
                <Input
                  value={designForm.associatedInst}
                  onChange={onDesignChange('associatedInst')}
                  placeholder="Leave blank for one school. Example: KU;KSU"
                />
              </Field>
              <Row style={{ marginTop: 12 }}>
                <Button type="button" onClick={saveDesign} disabled={savingDesign}>
                  {savingDesign ? 'Saving…' : editingId ? 'Save changes' : 'Add design'}
                </Button>
                {editingId && (
                  <Button type="button" $ghost onClick={() => { setEditingId(null); setDesignForm(emptyDesign); }}>
                    Cancel
                  </Button>
                )}
              </Row>
            </Card>
            <Card>
              <CardTitle>Catalog ({designs.length})</CardTitle>
              {designs.length === 0 && <Note>No licensed designs yet.</Note>}
              {designs.length > 0 && (
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>Design</th>
                        <th>UPI</th>
                        <th>School</th>
                        <th>Category</th>
                        <th>Description</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {designs.map((design) => (
                        <tr key={design.id}>
                          <td>
                            {design.designName}
                            {design.garmentNote ? <div><Muted>{design.garmentNote}</Muted></div> : null}
                            {design.associatedInst ? <div><Muted>Also {design.associatedInst}</Muted></div> : null}
                          </td>
                          <td>{design.upi}</td>
                          <td>{design.institutionShortCode}</td>
                          <td>{design.categorySubCategoryCode}</td>
                          <td>{design.prodDescription}</td>
                          <td>
                            <Danger type="button" onClick={() => startEdit(design)}>Edit</Danger>
                            {' · '}
                            <Danger type="button" onClick={() => removeDesign(design)}>Remove</Danger>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </Card>
          </>
        )}

        {tab === 'settings' && settings && (
          <>
            <Card>
              <CardTitle>Strawberry Patch retail customer</CardTitle>
              <Note>
                Only orders for this customer are reported. The production facility is ignored.
              </Note>
              {settings.customerId && (
                <Note>Linked customer: {settings.customerName || settings.customerId}</Note>
              )}
              <Field $wide>
                Search customers
                <Input
                  value={customerQuery}
                  onChange={(e) => setCustomerQuery(e.target.value)}
                  placeholder="Strawberry Patch"
                />
              </Field>
              <SearchList>
                {!customerQuery.trim() && <Note style={{ padding: 10 }}>Type the store name to find the customer.</Note>}
                {filteredCustomers.map((customer) => (
                  <SearchItem
                    key={customer.id}
                    type="button"
                    onClick={() => setSettings((prev) => ({
                      ...prev,
                      customerId: customer.id,
                      customerName: customer.client || ''
                    }))}
                  >
                    {customer.client || customer.id}
                  </SearchItem>
                ))}
                {filteredCustomers.length === 0 && <Note style={{ padding: 10 }}>No customers match.</Note>}
              </SearchList>
            </Card>
            <Card>
              <CardTitle>Store profile on the royalty file</CardTitle>
              <Note>Used for every row. No commas. This is the ship-to CLC expects for your own store.</Note>
              {settingsError && <Warn>{settingsError}</Warn>}
              <Row>
                <Field $grow>
                  Retailer name
                  <Input value={settings.retailerName} onChange={(e) => setSettings({ ...settings, retailerName: e.target.value })} />
                </Field>
                <Field $grow>
                  CLC retailer code
                  <Input value={settings.clcRetailerCode} onChange={(e) => setSettings({ ...settings, clcRetailerCode: e.target.value })} />
                </Field>
              </Row>
              <Row>
                <Field $grow>
                  Street
                  <Input value={settings.address} onChange={(e) => setSettings({ ...settings, address: e.target.value })} />
                </Field>
                <Field $grow>
                  City
                  <Input value={settings.city} onChange={(e) => setSettings({ ...settings, city: e.target.value })} />
                </Field>
                <Field>
                  State
                  <Input value={settings.state} onChange={(e) => setSettings({ ...settings, state: e.target.value })} maxLength={2} />
                </Field>
                <Field>
                  ZIP
                  <Input value={settings.zip} onChange={(e) => setSettings({ ...settings, zip: e.target.value })} />
                </Field>
              </Row>
              <Button type="button" onClick={saveSettings} disabled={savingSettings}>
                {savingSettings ? 'Saving…' : 'Save customer and store profile'}
              </Button>
            </Card>
            <Card>
              <CardTitle>Minimum royalty rates</CardTitle>
              <Note>
                Optional. Auburn’s example is 12 percent or $0.55 per tee, so the floor applies under $4.58.
                At about $8.50 wholesale, most lines stay in royalty sales. A line has one price, so the whole line is one or the other.
              </Note>
              {rateError && <Warn>{rateError}</Warn>}
              <Row>
                <Field>
                  School
                  <Input value={rateForm.institutionShortCode} onChange={(e) => setRateForm({ ...rateForm, institutionShortCode: e.target.value })} />
                </Field>
                <Field>
                  Category
                  <Input value={rateForm.categoryCode} onChange={(e) => setRateForm({ ...rateForm, categoryCode: e.target.value })} placeholder="01A" />
                </Field>
                <Field>
                  Royalty percent
                  <Input value={rateForm.royaltyPercent} onChange={(e) => setRateForm({ ...rateForm, royaltyPercent: e.target.value })} placeholder="12" />
                </Field>
                <Field>
                  MR/U dollars
                  <Input value={rateForm.mruAmount} onChange={(e) => setRateForm({ ...rateForm, mruAmount: e.target.value })} placeholder="0.55" />
                </Field>
                <Button type="button" onClick={addRate}>Add rate</Button>
              </Row>
              {rates.length > 0 && (
                <Table>
                  <thead>
                    <tr>
                      <th>School</th>
                      <th>Category</th>
                      <th>Percent</th>
                      <th>MR/U</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.map((rate) => (
                      <tr key={rate.id}>
                        <td>{rate.institutionShortCode}</td>
                        <td>{rate.categoryCode}</td>
                        <td>{rate.royaltyPercent}%</td>
                        <td>${Number(rate.mruAmount).toFixed(2)}</td>
                        <td>
                          <Danger type="button" onClick={async () => {
                            await deleteLicenseRate(db, rate.id);
                            setRates(await listLicenseRates(db));
                          }}>
                            Remove
                          </Danger>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </>
        )}
      </Inner>
    </Page>
  );
}
